/**
 * @typedef { import('./EventWebinarDetail').EventWebinarDetail } EventWebinarDetail
 */

import styled from 'styled-components';
import { DateTime, Duration } from 'luxon';
import { useCallback, useState, useEffect, useRef } from 'react';
import { getWindow } from 'ssr-window';
import countryList from 'country-list';
import { useLocalization } from '../../custom-hooks/useLocalization';
import ButtonLink from '../../shared/ButtonLinkComponent';
import { useEventWebinarContext } from './EventWebinarContext';
import BdoModal from '../../shared/BdoModal';
import TextInput from '../../shared/FormComponents/TextInput';
import { STATES } from '../../../constants/states';
import FormSelect from '../../shared/FormComponents/FormSelect';

const Card = styled.div`
  box-shadow: var(--box-shadow);
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 1.6rem;
  padding: 2.4rem 1.6rem;
  margin-bottom: 4rem;

  .btn-download {
    text-transform: uppercase;
    width: 100%;
  }
`;

const Tag = styled.p`
  border: 1px solid var(--grey);
  border-radius: 4px;
  color: var(--slate);
  display: flex;
  font-size: 1.2rem;
  letter-spacing: 0.5px;
  font-weight: 800;
  line-height: 1.6rem;
  margin: 0;
  padding: 0.4rem;
  text-transform: uppercase;
`;

const StyledSameDay = styled.div`
  time {
    color: var(--charcoal);
  }

  time:first-child {
    font-size: 2rem;
    font-weight: 800;
    line-height: 2.4rem;
  }

  time:last-child {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 2.4rem;
  }
`;

const StyledDifferentDay = styled.div`
  font-size: 2rem;
  font-weight: 800;
  line-height: 2.4rem;

  time {
    display: inline;
    white-space: nowrap;
  }
`;

const LineBreak = styled.hr`
  background: var(--grey);
  border: none;
  height: 1px;
  margin-block: 0;
  width: 100%;
`;

const Address = styled.address`
  font-style: normal;
  font-size: 1.6rem;
  line-height: 2.4rem;
`;

const Button = styled.button`
  padding-block: 1rem;
  font-size: 1.6rem;
  line-height: 2.4rem;
  text-transform: uppercase;
  font-weight: 700;
  width: 100%;
  display: block;
`;

const ButtonLinkStyled = styled(ButtonLink)`
  padding-block: 1rem;
  font-size: 1.6rem;
  line-height: 2.4rem;
  text-transform: uppercase;
  font-weight: 700;
  width: 100%;
  display: block;

  a,
  button {
    width: 100%;
  }
`;

const ButtonWithMargin = styled(Button)`
  border: none;
  margin-top: 2.4rem;
  white-space: normal;
  word-break: break-word;
`;

const StyledBdoModal = styled(BdoModal)`
  border: none;
  position: relative;

  &::backdrop {
    background: rgba(91, 110, 127, 0.5);
  }

  .btn {
    border: none;
    appearance: none;
    text-transform: uppercase;
  }
`;

const StyledTitle = styled.p`
  font-size: 3.2rem;
  line-height: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: min(80rem, 100%);

  .submit {
    border: 0;
    margin-top: 2rem;

    &:disabled {
      cursor: default;
      pointer-events: none;
    }
  }
`;

const ResponseContainer = styled.div`
  display: grid;
  grid: auto auto / 1fr;
  gap: 3rem;
  width: min(80rem, 100%);
`;

const EMPTY_FORM_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  phone: '',
  title: '',
  city: '',
  state: '',
  independentMember: '',
  question1: '',
  question2: '',
  question3: ''
};

// function that gets value in querystring by key case insensitive
const getQueryStringValue = (key) => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const value = urlParams.keys().find((k) => k.toLowerCase() === key.toLowerCase());

  return value ? urlParams.get(value) : null;
};

// function gets all utm codes from querystring
const getUtmCodes = () => {
  const utmCodes = {};
  const utmSource = getQueryStringValue('utm_source');
  const utmMedium = getQueryStringValue('utm_medium');
  const utmCampaign = getQueryStringValue('utm_campaign');
  const utmTerm = getQueryStringValue('utm_term');
  const utmContent = getQueryStringValue('utm_content');

  if (utmSource) {
    utmCodes.utm_source = utmSource;
  }
  if (utmMedium) {
    utmCodes.utm_medium = utmMedium;
  }
  if (utmCampaign) {
    utmCodes.utm_campaign = utmCampaign;
  }
  if (utmTerm) {
    utmCodes.utm_term = utmTerm;
  }
  if (utmContent) {
    utmCodes.utm_content = utmContent;
  }
  return utmCodes;
};

const DetailsAndRegistrationCard = ({ className, eventTypeDisplayName }) => {
  const { culture, resourceStrings } = useLocalization();
  const {
    attendee,
    dateFrom,
    dateTo,
    venue,
    street,
    street2,
    city,
    state,
    postalCode,
    timeZone,
    isUserAuthorized,
    isRegistered,
    showArchivedWebinar,
    recordingUrl,
    marketoProgramId,
    registrationButtonUrl,
    eventWebinarDetailGuid,
    isLoggedIn,
    title,
    question1,
    question2,
    question3,
    recaptchaSiteKey,
    disableRegistrationButton
  } = useEventWebinarContext();

  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const eventRegisterText = resourceStrings['bdO.EventsWebinars.Register'];
  const from = DateTime.fromISO(dateFrom);
  const to = DateTime.fromISO(dateTo);
  const timeFrom = from.toLocaleString(DateTime.TIME_SIMPLE, { locale: culture });
  const timeTo = to.toLocaleString(DateTime.TIME_SIMPLE, { locale: culture });
  const fromDateFull = from.toLocaleString(DateTime.DATE_FULL, { locale: culture });
  const toDateFull = to.toLocaleString(DateTime.DATE_FULL, { locale: culture });
  const duration = Duration.fromMillis(from.diff(to).milliseconds).toString();
  const isSameDay = from.hasSame(to, 'day');
  const isEventInFuture = to.diffNow() > 0;
  const googleMapsSearchUrl = `https://www.google.com/maps/search/${street} ${city}, ${state} ${postalCode}`;
  const window = getWindow();
  const [url, setUrl] = useState(null);
  const [formState, setFormState] = useState(EMPTY_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef(null);
  const [registrationResponseText, setRegistrationResponseText] = useState('');
  const [isRegisteredState, setIsRegisteredState] = useState(isRegistered);
  let previousPhoneValue = '';

  useEffect(() => {
    if (attendee && isLoggedIn) {
      let attendeeWithoutNullValues = {};
      Object.keys(attendee).forEach((key) => {
        if (attendee[key] === null) {
          attendeeWithoutNullValues = { ...attendeeWithoutNullValues, [key]: '' };
        } else if (key === 'state') {
          // map state to state
          const stateCode = Object.keys(STATES).find(
            (stateKey) => STATES[stateKey] === attendee[key]
          );
          attendeeWithoutNullValues = {
            ...attendeeWithoutNullValues,
            [key]: stateCode || attendee[key]
          };
        } else {
          attendeeWithoutNullValues = { ...attendeeWithoutNullValues, [key]: attendee[key] };
        }
      });
      setFormState(attendeeWithoutNullValues);

      const attendeeCountry = attendee.country ? attendee.country : '';

      setSelectedCountry(
        attendee.country === 'USA' ||
          attendee.country === 'US' ||
          attendee.country === 'United States'
          ? 'United States of America'
          : attendeeCountry
      );
    }
  }, [attendee]);

  useEffect(() => {
    const countries = countryList
      .getNames()
      .map((country) => ({
        name: country,
        value: country
      }))
      .filter((country) => country.name !== 'United States of America');

    const usa = countryList.getName('US');

    const alphabeticalCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
    countries.unshift({ name: usa, value: usa }); // add USA to the top of the list

    setCountryOptions(alphabeticalCountries);
  }, []);

  const SameDayDisplay = useCallback(
    () => (
      <StyledSameDay>
        <time suppressHydrationWarning dateTime={dateFrom}>
          {fromDateFull}
        </time>
        <br />
        <time suppressHydrationWarning dateTime={duration}>
          {timeFrom} to {timeTo} {timeZone}
        </time>
      </StyledSameDay>
    ),
    [dateFrom, fromDateFull, duration, timeFrom, timeTo, timeZone]
  );

  const DifferentDayDisplay = useCallback(
    () => (
      <StyledDifferentDay>
        <time suppressHydrationWarning dateTime={dateFrom}>
          {fromDateFull}
        </time>
        &ensp;&#8211;&ensp;
        <time suppressHydrationWarning dateTime={dateTo}>
          {toDateFull}
        </time>
      </StyledDifferentDay>
    ),
    [dateFrom, dateTo, fromDateFull, toDateFull]
  );
  useEffect(() => {
    setUrl(window.location.origin);
  }, [window]);

  const handleInput = (event) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      [event.target.name]: event.target.value
    }));
  };

  const handlePhoneInput = (event) => {
    if (!document.getElementById('international-phone-check').checked) {
      const x = event.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      // eslint-disable-next-line prefer-template
      const newValue = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
      setFormState((prevFormState) => ({
        ...prevFormState,
        [event.target.name]: newValue
      }));
    } else {
      if (event.target.value !== '') {
        const txt = event.target.value.match(/^[0-9+\-/]+$/g, '');
        if (!txt) {
          document.getElementById('phone').value = previousPhoneValue;
          return;
        }
      }
      previousPhoneValue = event.target.value;
      setFormState((prevFormState) => ({
        ...prevFormState,
        [event.target.name]: event.target.value
      }));
    }
  };

  const handlePhoneKeyDown = (event) => {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode === 43 || charCode === 45 || charCode === 189 || charCode === 187) {
      return true;
    }
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  };

  const handleIntlPhoneSelect = () => {
    if (document.getElementById('international-phone-check').checked) {
      document.getElementById('phone-label').innerText = 'International Phone Number';
      document.getElementById('phone').value = document
        .getElementById('phone')
        .value.replaceAll('(', '')
        .replaceAll(')', '');
    } else {
      document.getElementById('phone-label').innerText = 'Phone Number';
      const x = document
        .getElementById('phone')
        .value.replace(/\D/g, '')
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      // eslint-disable-next-line prefer-template
      const newValue = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
      document.getElementById('phone-label').value = newValue;
    }
  };

  const handleCountrySelection = (event) => {
    setSelectedCountry(event.target.value);
    handleInput(event);
  };

  const closeModal = () => {
    setIsOpen(false);
    setRegistrationResponseText('');
  };

  const registrationFormHeading = (eventTitle, headingString) =>
    headingString?.replace('{0}', eventTitle);

  const handleRegistrationForm = async () => {
    setIsSubmitting(true);
    setIsOpen(true);
    setRegistrationResponseText('');
    try {
      const token = await new Promise((resolve, reject) =>
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(recaptchaSiteKey, {
              action: 'Registration'
            })
            .then(resolve)
            .catch(reject);
        })
      );

      const body = {
        ...formState,
        eventGuid: eventWebinarDetailGuid,
        marketoProgramId,
        gToken: token,
        utmCodes: JSON.stringify(getUtmCodes())
      };

      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append(
        'RequestVerificationToken',
        document.getElementsByName('__RequestVerificationToken')[0].value
      );

      await fetch('/api/events-webinars/register-form', {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      }).then((response) => {
        if (response.ok) {
          setRegistrationResponseText(resourceStrings['bdO.EventsWebinars.Register.Success']);
          setIsRegisteredState(true);
          setFormState(EMPTY_FORM_STATE);
        } else {
          setRegistrationResponseText(resourceStrings['bdO.EventsWebinars.Register.Failure']);
        }
      });
    } catch (e) {
      setRegistrationResponseText(resourceStrings['bdO.EventsWebinars.Register.Failure']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnregistration = async () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append(
      'RequestVerificationToken',
      document.getElementsByName('__RequestVerificationToken')[0].value
    );

    const token = await new Promise((resolve, reject) =>
      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute(recaptchaSiteKey, {
            action: 'Registration'
          })
          .then(resolve)
          .catch(reject);
      })
    );

    const body = {
      eventGuid: eventWebinarDetailGuid,
      marketoProgramId,
      gToken: token
    };

    fetch('/api/events-webinars/unregister', {
      method: 'POST',
      body: JSON.stringify(body),
      headers
    })
      .then((response) => {
        setIsOpen(true);
        if (response.ok) {
          setRegistrationResponseText(resourceStrings['bdO.EventsWebinars.Unregister.Success']);
        } else {
          setRegistrationResponseText(resourceStrings['bdO.EventsWebinars.Unregister.Error']);
        }
        return response.json();
      })
      .then((data) => {
        setIsRegisteredState(data.isRegistered);
      });
  };

  const handleNonLoggedInRegistration = async (event) => {
    event.preventDefault();

    const isValid = formRef.current.checkValidity();
    formRef.current.checkValidity();

    if (!isValid) return;

    await handleRegistrationForm();
  };
  return (
    <Card className={className}>
      <Tag>{eventTypeDisplayName}</Tag>
      {isSameDay ? <SameDayDisplay /> : <DifferentDayDisplay />}
      {/* eslint-disable-next-line no-nested-ternary */}
      {showArchivedWebinar && recordingUrl ? (
        <ButtonWithMargin
          className="btn btn-primary non-default-link"
          as="a"
          href={recordingUrl}
          target="_blank"
        >
          {resourceStrings['bdO.EventsWebinars.ViewRecording']}
        </ButtonWithMargin>
      ) : (
        <>
          {city && (
            <>
              <LineBreak />
              <Address>
                {venue && (
                  <>
                    <span
                      style={{
                        fontWeight: 700
                      }}
                    >
                      {venue}
                    </span>
                    <br />
                  </>
                )}
                <span>{street}</span>
                <br />
                {street2 && (
                  <>
                    <span>{street2}</span>
                    <br />
                  </>
                )}
                <span>
                  {city}, {state} {postalCode}
                </span>
              </Address>
              <ButtonLink
                type="icon-tertiary"
                text="Get Directions"
                link={googleMapsSearchUrl}
                target="_blank"
                icon="place"
              />
            </>
          )}
          {isLoggedIn &&
            isEventInFuture &&
            eventTypeDisplayName.toLowerCase() === 'event' &&
            isRegisteredState && (
              <ButtonLinkStyled
                type="primary"
                text={resourceStrings['bdO.EventsWebinars.Unregister']}
                isButtonTag={true}
                target="_blank"
                onClickFunction={handleUnregistration}
              />
            )}
          {isEventInFuture && !isRegisteredState && disableRegistrationButton && (
            <ButtonLinkStyled
              type="primary"
              text={resourceStrings['bdO.EventsWebinars.DisabledRegisterButton']}
              isButtonTag={true}
              isDisabled={true}
              ariaAttributes={{
                'aria-disabled': 'true'
              }}
            />
          )}
          {isEventInFuture &&
            !disableRegistrationButton &&
            eventTypeDisplayName.toLowerCase() === 'event' &&
            !isRegisteredState &&
            registrationButtonUrl === '' && (
              <ButtonLinkStyled
                id="event-registration-button"
                type="primary"
                text={eventRegisterText}
                isButtonTag={true}
                target="_blank"
                onClickFunction={() => setIsOpen(true)}
              />
            )}
          {isEventInFuture && !disableRegistrationButton && registrationButtonUrl !== '' && (
            <ButtonLinkStyled
              type="primary"
              text={eventRegisterText}
              link={registrationButtonUrl}
              target="_blank"
            />
          )}
          {isEventInFuture && eventTypeDisplayName.toLowerCase() === 'event' && (
            <>
              <StyledBdoModal
                showModal={isOpen}
                setShowModal={() => closeModal()}
                classProp="bdo-modal simple"
              >
                {registrationResponseText && (
                  <ResponseContainer>
                    <StyledTitle>{registrationResponseText}</StyledTitle>
                  </ResponseContainer>
                )}
                {!isRegisteredState && !registrationResponseText && (
                  <>
                    <StyledTitle>
                      {registrationFormHeading(
                        title,
                        resourceStrings['bdO.NonLoggedInEventRegistration.FormHeading']
                      )}
                    </StyledTitle>
                    <StyledForm id="non-logged-in-user-reg" ref={formRef}>
                      <TextInput
                        id="first-name"
                        name="firstName"
                        label={resourceStrings['bdO.NonLoggedInEventRegistration.FirstNameLabel']}
                        autoComplete="given-name"
                        onInput={handleInput}
                        value={formState.firstName}
                        disabled={isSubmitting}
                        required
                      />
                      <TextInput
                        id="last-name"
                        name="lastName"
                        label={resourceStrings['bdO.NonLoggedInEventRegistration.LastNameLabel']}
                        autoComplete="family-name"
                        value={formState.lastName}
                        onInput={handleInput}
                        disabled={isSubmitting}
                        required
                      />
                      <TextInput
                        id="email"
                        name="email"
                        label={resourceStrings['bdO.NonLoggedInEventRegistration.EmailLabel']}
                        type="email"
                        autoComplete="email"
                        pattern="[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+"
                        onInput={handleInput}
                        value={formState.email}
                        disabled={isSubmitting}
                        errorMessages={{
                          patternMismatch: 'Please enter a valid email address.',
                          typeMismatch: 'Please enter a valid email address.'
                        }}
                        required
                      />
                      <div className="custom-checkbox">
                        <input
                          id="international-phone-check"
                          name="international-phone-check"
                          type="checkbox"
                          onChange={handleIntlPhoneSelect}
                        />
                        <label htmlFor="international-phone-check">
                          {
                            resourceStrings[
                              'bdO.NonLoggedInEventRegistration.InternationalPhoneSelect'
                            ]
                          }
                        </label>
                      </div>
                      <TextInput
                        id="phone"
                        name="phone"
                        label={resourceStrings['bdO.NonLoggedInEventRegistration.PhoneNumberLabel']}
                        autoComplete="tel"
                        onInput={handlePhoneInput}
                        onKeyDown={handlePhoneKeyDown}
                        value={formState.phone}
                        disabled={isSubmitting}
                      />
                      <TextInput
                        id="company"
                        name="company"
                        label={
                          resourceStrings[
                            'bdO.NonLoggedInEventRegistration.CompanyOrganizationLabel'
                          ]
                        }
                        autoComplete="organization"
                        value={formState.company}
                        onInput={handleInput}
                        disabled={isSubmitting}
                        required
                      />
                      <TextInput
                        id="title"
                        name="title"
                        label={resourceStrings['bdO.NonLoggedInEventRegistration.JobTitleLabel']}
                        autoComplete="organization-title"
                        value={formState.title}
                        onInput={handleInput}
                        disabled={isSubmitting}
                        required
                      />
                      <div className="form-field">
                        <FormSelect
                          options={countryOptions}
                          name="country"
                          selectId="country"
                          onChangeFunction={(e) => handleCountrySelection(e)}
                          label={resourceStrings['bdO.Registration.CountryLabel']}
                          placeholder={resourceStrings['bdO.Registration.CountryLabel']}
                          selectedOption={selectedCountry}
                          required
                        />
                      </div>
                      {selectedCountry === 'United States of America' && (
                        <>
                          <TextInput
                            id="city"
                            name="city"
                            label={resourceStrings['bdO.NonLoggedInEventRegistration.CityLabel']}
                            value={formState.city}
                            onInput={handleInput}
                            disabled={isSubmitting}
                            required
                          />
                          <FormSelect
                            label={resourceStrings['bdO.NonLoggedInEventRegistration.StateLabel']}
                            name="state"
                            selectId="state"
                            autoComplete="address-level1"
                            placeholder="State"
                            selectedOption={formState?.state}
                            disabled={isSubmitting}
                            onChangeFunction={handleInput}
                            options={Object.keys(STATES).map((usStateKey) => ({
                              name: STATES[usStateKey],
                              value: usStateKey
                            }))}
                            required
                          />
                        </>
                      )}
                      <div className="custom-checkbox">
                        <input
                          id="allianceMember"
                          name="allianceMember"
                          type="checkbox"
                          value={formState.isAllianceClient}
                          onChange={() =>
                            setFormState({
                              ...formState,
                              isAllianceClient: !formState.isAllianceClient
                            })
                          }
                          checked={formState.isAllianceClient}
                        />
                        <label htmlFor="allianceMember">
                          {
                            resourceStrings[
                              'bdO.NonLoggedInEventRegistration.IndependentMemberLabel'
                            ]
                          }
                        </label>
                      </div>
                      {question1 && (
                        <TextInput
                          id="question1"
                          name="question1"
                          label={question1}
                          value={formState.question1}
                          onInput={handleInput}
                          disabled={isSubmitting}
                          required
                        />
                      )}
                      {question2 && (
                        <TextInput
                          id="question2"
                          name="question2"
                          label={question2}
                          value={formState.question2}
                          onInput={handleInput}
                          disabled={isSubmitting}
                          required
                        />
                      )}
                      {question3 && (
                        <TextInput
                          id="question3"
                          name="question3"
                          label={question3}
                          value={formState.question3}
                          onInput={handleInput}
                          disabled={isSubmitting}
                          required
                        />
                      )}
                      <button
                        data-sitekey="test"
                        className="btn btn-primary submit"
                        value="submit"
                        disabled={isSubmitting}
                        onClick={(event) => {
                          if (!isSubmitting) handleNonLoggedInRegistration(event);
                        }}
                      >
                        {isSubmitting
                          ? resourceStrings['bdO.NonLoggedInEventRegistration.SubmitButton.Loading']
                          : resourceStrings['bdO.NonLoggedInEventRegistration.SubmitButton']}
                      </button>
                    </StyledForm>
                  </>
                )}
              </StyledBdoModal>
            </>
          )}
          {isUserAuthorized && (
            <a
              className="btn btn-primary btn-download non-default-link"
              href={`${url}/api/GetLeads?programId=${marketoProgramId}`}
              target="_blank"
            >
              {resourceStrings['bdO.EventsWebinars.FileDownloadText']}
            </a>
          )}
        </>
      )}
    </Card>
  );
};

export default DetailsAndRegistrationCard;
